/**
 * Redux reducer for the application.
 * @module redux/reducers/app
 */

import update from "immutability-helper";
import {
  CHANGE_SIDEBAR_STATE,
  SET_NCS_ID,
  SET_APP_MODE,
  LOAD_CONFIG,
  RECEIVED_CONFIG,
  LOAD_CONFIG_FAILED,
  LOAD_USER_CONFIG,
  RECEIVED_USER_CONFIG,
  LOAD_USER_CONFIG_FAILED,
  SET_RUN_CONTEXT,
  CHANGE_FOOTER_HEIGHT,
  UPDATE_CONNECTION_STATUS,
  SET_VIEWPORT_SIZE,
  SET_APP_RUN_MODE,
  SET_CONTAINER_TYPE,
  INITIALIZE,
  INITIALIZE_COMPLETE,
  INITIALIZE_FAILED,
  QUERY_CONNECTION_STATUS,
  QUERY_CONNECTION_STATUS_FAILED,
  OPEN_PLACEHOLDER
} from "../actions/app";
import { INSERT_MOS_OBJECT_SUCCESS } from "../actions/asset";
import AppMode from "../constants/appMode";
import SidebarState from "../constants/sidebarState";
import RunContext from "../constants/runContext";
import AppRunMode from "../constants/appRunMode";
import ContainerType from "../constants/containerType";
import { mergeWithDefaultConfig } from "../util/userConfig";

const initialState = {
  initialized: false,
  initializing: false,
  sidebar: SidebarState.LEFT_ONLY,
  ncsId: null,
  appMode: AppMode.NORMAL,
  appRunMode: AppRunMode.BROWSER,
  containerType: ContainerType.NONE,
  runContext: RunContext.DEFAULT,
  footerHeight: "5rem",
  viewPortSize: 1000,
  config: {},
  configLoading: false,
  configLoaded: false,
  configError: false,
  userConfigLoading: false,
  connections: {
    mediaEngine: {},
    axis: {},
    camio: {},
    publishing: {}
  },
  connectionStatusLoading: false,
  placeholderId: ""
};

const ACTION_HANDLERS = {
  [INITIALIZE]: state => ({
    ...state,
    initializing: true,
    initialized: false
  }),
  [INITIALIZE_COMPLETE]: state => ({
    ...state,
    initializing: false,
    initialized: true
  }),
  [INITIALIZE_FAILED]: state => ({
    ...state,
    initializing: false,
    initialized: false
  }),
  [CHANGE_FOOTER_HEIGHT]: (state, action) => ({
    ...state,
    footerHeight: action.payload.footerHeight
  }),
  [CHANGE_SIDEBAR_STATE]: (state, action) => ({
    ...state,
    sidebar: action.payload.sidebarState
  }),
  [SET_NCS_ID]: (state, action) => ({
    ...state,
    ncsId: action.payload.ncsId
  }),
  [SET_APP_MODE]: (state, action) => ({
    ...state,
    appMode: action.payload.appMode
  }),
  [SET_RUN_CONTEXT]: (state, action) => ({
    ...state,
    runContext: action.payload.runContext
  }),
  [LOAD_CONFIG]: state => ({
    ...state,
    configLoading: true,
    configError: false
  }),
  [RECEIVED_CONFIG]: (state, action) => ({
    ...state,
    configLoading: false,
    config: action.payload.config,
    configError: false,
    configLoaded: true
  }),
  [LOAD_CONFIG_FAILED]: state => ({
    ...state,
    configLoading: false,
    configError: true
  }),
  [LOAD_USER_CONFIG]: state => ({
    ...state,
    userConfigLoading: true,
    configError: false
  }),
  [RECEIVED_USER_CONFIG]: (state, action) =>
    update(state, {
      userConfigLoading: { $set: false },
      configError: { $set: false },
      config: {
        userConfig: {
          $set: mergeWithDefaultConfig(
            state.config.defaultUserConfig,
            action.payload.userConfig
          )
        }
      }
    }),
  [LOAD_USER_CONFIG_FAILED]: state => ({
    ...state,
    userConfigLoading: false,
    configError: true
  }),
  [QUERY_CONNECTION_STATUS]: (state, action) => {
    if (action.payload && action.payload.connection) {
      return {
        ...state,
        connections: {
          [action.payload.connection]: {
            loading: true
          }
        }
      };
    }
    return {
      ...state,
      connectionStatusLoading: true
    };
  },
  [QUERY_CONNECTION_STATUS_FAILED]: (state, action) => {
    if (action.payload && action.payload.connection) {
      return {
        ...state,
        connections: {
          [action.payload.connection]: {
            loading: false
          }
        }
      };
    }
    return {
      ...state,
      connectionStatusLoading: false
    };
  },
  [UPDATE_CONNECTION_STATUS]: (state, action) => {
    if (action.payload.connections) {
      return {
        ...state,
        connections: action.payload.connections
      };
    }
    return update(state, {
      connections: {
        [action.payload.connection]: {
          connected: { $set: action.payload.connected },
          address: { $set: action.payload.address },
          loading: false
        }
      }
    });
  },
  [SET_VIEWPORT_SIZE]: (state, action) => ({
    ...state,
    viewPortSize: action.payload.size
  }),
  [SET_APP_RUN_MODE]: (state, action) => ({
    ...state,
    appRunMode: action.payload.appRunMode
  }),
  [SET_CONTAINER_TYPE]: (state, action) => ({
    ...state,
    containerType: action.payload.containerType
  }),
  [OPEN_PLACEHOLDER]: (state, action) => ({
    ...state,
    placeholderId: action.payload.placeholderId
  }),
  [INSERT_MOS_OBJECT_SUCCESS]: state => ({
    ...state,
    placeholderId: ""
  })
};

export default function app(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type];

  return handler ? handler(state, action) : state;
}



// WEBPACK FOOTER //
// ./app/reducers/app.js